import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RotateCcw, Check, AlertTriangle, ChevronDown, ChevronUp, Building2 } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";
import { usePdaScan } from "@/hooks/usePdaScan";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stocktake — warehouse or hotel scope.
 *
 * On entering the tab (or picking a hotel) the FULL expected inventory for
 * the scope is preloaded once (compact [[rfid, product_id]] snapshot), so
 * every scan is compared locally and the stats update instantly — no
 * per-second server round trips even with tens of thousands of tags.
 * Tags outside the expected set (anomalies/unknown) are resolved in small
 * debounced lookups. Commit stays server-side (authoritative).
 */
export default function PdaStocktake({ onLogout, mode = "warehouse" }) {
  const navigate = useNavigate();
  const isHotel = mode === "hotel";
  const [hotel, setHotel] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [rfids, setRfids] = useState(() => new Set());
  const [committing, setCommitting] = useState(false);
  // Preloaded expected set: Map(rfid -> product_id) + product names
  const [expected, setExpected] = useState(null); // {map, products}
  const [expectedLoading, setExpectedLoading] = useState(false);
  // Resolution of scanned tags NOT in the expected set
  const [outsideInfo, setOutsideInfo] = useState({}); // rfid -> item | null(unknown)

  usePdaScenarioHint("Stocktake");
  useEffect(() => { primeAudio(); }, []);

  // Hotel mode: load the hotel list for the picker.
  useEffect(() => {
    if (!isHotel) return;
    setHotelsLoading(true);
    axios.get(`${API}/hotels`)
      .then((res) => {
        const rows = (res.data || [])
          .map((h) => ({ id: h.id, name: h.hotel_name || h.owner_email || "Hotel", suburb: h.suburb || "" }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setHotels(rows);
      })
      .catch(() => toast.error("Couldn't load hotels"))
      .finally(() => setHotelsLoading(false));
  }, [isHotel]);

  // ── PRELOAD the expected inventory as soon as the scope is known ──
  useEffect(() => {
    if (isHotel && !hotel) { setExpected(null); return; }
    let cancelled = false;
    setExpectedLoading(true);
    const params = isHotel ? { hotel_id: hotel.id } : {};
    axios.get(`${API}/linen/stocktake/expected`, { params, headers: { "x-no-cache": "1" } })
      .then((res) => {
        if (cancelled) return;
        const map = new Map((res.data.items || []).map(([r, pid]) => [r, pid]));
        setExpected({ map, products: res.data.products || {} });
      })
      .catch(() => toast.error("Couldn't preload inventory"))
      .finally(() => { if (!cancelled) setExpectedLoading(false); });
    return () => { cancelled = true; };
  }, [isHotel, hotel]);

  const rfidsRef = useRef(new Set());
  const lookedUpRef = useRef(new Set());
  const clearRfids = useCallback(() => {
    rfidsRef.current = new Set();
    lookedUpRef.current = new Set();
    setRfids(new Set());
    setOutsideInfo({});
  }, []);

  const onEpcs = useCallback((epcs) => {
    if (!epcs || epcs.length === 0) return;
    if (isHotel && !hotel) return;
    let added = 0;
    for (const e of epcs) {
      if (!rfidsRef.current.has(e)) { rfidsRef.current.add(e); added++; }
    }
    if (added > 0) {
      setRfids(new Set(rfidsRef.current));
      beep.tick();
    }
  }, [isHotel, hotel]);
  usePdaScan(onEpcs, { enabled: !isHotel || !!hotel });

  // Resolve scanned tags that are OUTSIDE the expected set (small batches).
  useEffect(() => {
    if (!expected) return;
    const pending = Array.from(rfids).filter(
      (r) => !expected.map.has(r) && !lookedUpRef.current.has(r)
    );
    if (pending.length === 0) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/linen/lookup-many`, { rfids: pending });
        const next = {};
        for (const it of res.data.items || []) next[it.rfid] = it;
        for (const r of res.data.unknown || []) next[r] = null;
        for (const r of pending) lookedUpRef.current.add(r);
        setOutsideInfo((prev) => ({ ...prev, ...next }));
      } catch { /* retried on next scan */ }
    }, 600);
    return () => clearTimeout(t);
  }, [rfids, expected]);

  // ── LOCAL live diff — instant, no server round trips ──
  const diff = useMemo(() => {
    if (!expected) return null;
    const products = expected.products;
    const matchedByPid = new Map();
    let matchedCount = 0;
    const missingRfids = [];
    const anomalies = [];   // scanned, exists elsewhere
    const unknown = [];     // scanned, not registered
    let outsidePending = 0;

    for (const r of rfids) {
      if (expected.map.has(r)) {
        matchedCount++;
        const pid = expected.map.get(r);
        matchedByPid.set(pid, (matchedByPid.get(pid) || 0) + 1);
      } else if (r in outsideInfo) {
        if (outsideInfo[r] === null) unknown.push(r);
        else anomalies.push(outsideInfo[r]);
      } else {
        outsidePending++;
      }
    }
    for (const [r] of expected.map) {
      if (!rfids.has(r)) missingRfids.push(r);
    }
    const missingByPid = new Map();
    for (const r of missingRfids) {
      const pid = expected.map.get(r);
      missingByPid.set(pid, (missingByPid.get(pid) || 0) + 1);
    }
    const anomalyByProduct = groupByProduct(anomalies);
    const toRows = (m) => Array.from(m.entries())
      .map(([pid, count]) => ({ product_id: pid, product_name: products[pid] || "Unknown", count }))
      .sort((a, b) => b.count - a.count);
    return {
      matchedCount,
      matchedRows: toRows(matchedByPid),
      missingCount: missingRfids.length,
      missingRows: toRows(missingByPid),
      anomalies,
      anomalyByProduct,
      unknown,
      outsidePending,
    };
  }, [rfids, expected, outsideInfo]);

  const commit = async () => {
    if (!diff || committing || rfids.size === 0) return;
    const home = isHotel ? hotel.name : "warehouse";
    let msg = `Commit ${isHotel ? "hotel" : "warehouse"} stocktake?\n\n${diff.matchedCount} already at ${home}`;
    if (diff.anomalies.length > 0) msg += `\n${diff.anomalies.length} anomalies will be recorded under ${home}`;
    if (diff.unknown.length > 0) msg += `\n${diff.unknown.length} unknown tags (ignored — register later)`;
    if (diff.missingCount > 0) msg += `\n${diff.missingCount} will be flagged MISSING (auto-cleared on any future scan)`;
    if (!window.confirm(msg)) return;
    setCommitting(true);
    try {
      const body = { rfids: Array.from(rfids) };
      if (isHotel && hotel) body.hotel_id = hotel.id;
      const res = await axios.post(`${API}/linen/stocktake/commit`, body);
      beep.complete();
      toast.success(`Stocktake committed · ${res.data.flipped_count} item(s) ${isHotel ? "recorded to " + hotel.name : "recovered"}`);
      clearRfids();
      // Refresh the expected snapshot — the commit changed item locations.
      setExpected(null);
      const params = isHotel ? { hotel_id: hotel.id } : {};
      const fresh = await axios.get(`${API}/linen/stocktake/expected`, { params, headers: { "x-no-cache": "1" } });
      setExpected({ map: new Map((fresh.data.items || []).map(([r, pid]) => [r, pid])), products: fresh.data.products || {} });
    } catch {
      beep.error();
      toast.error("Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  // ─── Hotel picker screen ───
  if (isHotel && !hotel) {
    return (
      <PdaLayout title="Hotel Stocktake" subtitle="Pick a hotel first" onBack={() => navigate("/pda/menu")} onLogout={onLogout}>
        {hotelsLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>
        ) : (
          <div className="space-y-2" data-testid="pda-stocktake-hotel-picker">
            {hotels.map((h) => (
              <button
                key={h.id}
                data-testid={`pda-stocktake-hotel-${h.id}`}
                onClick={() => setHotel(h)}
                className="w-full rounded-md border bg-white px-3 py-3 text-left flex items-center gap-3 active:bg-violet-50"
              >
                <Building2 className="h-5 w-5 text-violet-600 shrink-0" />
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">{h.name}</div>
                  {h.suburb ? <div className="text-[11px] text-neutral-500">{h.suburb}</div> : null}
                </div>
              </button>
            ))}
            {hotels.length === 0 && <div className="text-center text-sm text-neutral-500 py-8">No hotels found</div>}
          </div>
        )}
      </PdaLayout>
    );
  }

  const expectedTotal = expected ? expected.map.size : null;

  return (
    <PdaLayout
      title={isHotel ? "Hotel Stocktake" : "Stocktake"}
      subtitle={isHotel ? hotel.name : "Warehouse count"}
      onBack={() => navigate("/pda/menu")}
      onLogout={onLogout}
      footer={
        <button
          onClick={commit}
          disabled={committing || rfids.size === 0 || !diff}
          data-testid="pda-stocktake-commit"
          className="w-full h-12 rounded-md bg-violet-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {committing ? <Loader2 className="h-5 w-5 animate-spin" /> : `Commit stocktake (${rfids.size} scanned)`}
        </button>
      }
    >
      {isHotel && (
        <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 mb-3 flex items-center justify-between" data-testid="pda-stocktake-hotel-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-violet-700 shrink-0" />
            <span className="text-sm font-bold text-violet-900 truncate">{hotel.name}</span>
          </div>
          <button
            onClick={() => { clearRfids(); setHotel(null); }}
            data-testid="pda-stocktake-change-hotel"
            className="text-[11px] text-violet-700 underline shrink-0 ml-2"
          >
            Change hotel
          </button>
        </div>
      )}

      {expectedLoading && (
        <div className="rounded-md border bg-sky-50 border-sky-200 px-3 py-2 mb-3 text-xs text-sky-800 flex items-center gap-2" data-testid="pda-stocktake-preloading">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preloading inventory for instant compare…
        </div>
      )}

      {/* Top stats — instant, computed locally */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <TopStat label="Expected" value={expectedTotal ?? "—"} tone="neutral" testid="pda-stocktake-stat-expected" />
        <TopStat label="Scanned" value={rfids.size} subtle={diff ? `${diff.matchedCount} matched` : null} tone="emerald" testid="pda-stocktake-stat-scanned" />
        <TopStat label="Missing" value={diff ? diff.missingCount : "—"} tone="rose" testid="pda-stocktake-stat-missing" />
      </div>

      <div className="rounded-md border bg-white px-3 py-2 mb-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Session</div>
          <div className="font-mono font-bold text-2xl">{rfids.size}</div>
        </div>
        <div className="text-[11px] text-neutral-500 text-right">
          Press the middle key to scan
          {rfids.size > 0 ? (
            <button onClick={clearRfids} data-testid="pda-stocktake-clear" className="block ml-auto mt-1 text-rose-700 underline flex items-center gap-1">
              <RotateCcw className="h-3 w-3" /> Clear
            </button>
          ) : null}
        </div>
      </div>

      {diff && rfids.size > 0 && (
        <div className="space-y-2">
          {/* Matched — live by product */}
          <div className="rounded-md border bg-emerald-50 border-emerald-200" data-testid="pda-stocktake-matched">
            <div className="px-3 py-2 border-b border-emerald-200 flex items-center justify-between text-emerald-800">
              <div className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5" /> At {isHotel ? "this hotel" : "warehouse"}
              </div>
              <div className="font-display font-bold text-2xl">{diff.matchedCount}</div>
            </div>
            {diff.matchedRows.length > 0 && (
              <div className="divide-y divide-emerald-200">
                {diff.matchedRows.map((b) => (
                  <div key={b.product_id} className="px-3 py-1.5 flex items-center justify-between">
                    <span className="text-sm font-medium text-emerald-900">{b.product_name}</span>
                    <span className="font-mono tabular-nums font-bold text-sm text-emerald-800">{b.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Anomalies — scanned but recorded elsewhere */}
          {(diff.anomalies.length > 0 || diff.outsidePending > 0) && (
            <div className="rounded-md border bg-amber-50 border-amber-300" data-testid="pda-stocktake-anomalies">
              <div className="px-3 py-2 border-b border-amber-200">
                <div className="flex items-center justify-between text-amber-900">
                  <div className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Not at {isHotel ? "this hotel" : "warehouse"}
                    {diff.outsidePending > 0 ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  </div>
                  <div className="font-display font-bold text-2xl">{diff.anomalies.length}</div>
                </div>
                <div className="text-[11px] text-amber-800 mt-1">
                  On commit they'll be recorded {isHotel ? `under ${hotel.name}` : "back at the warehouse"}.
                </div>
              </div>
              {diff.anomalyByProduct.length > 0 && (
                <div className="divide-y divide-amber-200">
                  {diff.anomalyByProduct.map((g) => (
                    <div key={g.product_id} className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-amber-900">{g.product_name}</span>
                        <span className="font-mono tabular-nums font-bold text-sm text-amber-800">{g.items.length}</span>
                      </div>
                      <div className="text-[10px] text-amber-700 mt-0.5">{summariseLocations(g.items)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Unknown tags */}
          {diff.unknown.length > 0 && <UnknownCollapsible unknown={diff.unknown} testid="pda-stocktake-live-unknown" />}

          {/* Missing — expected but not scanned */}
          <div className="rounded-md border bg-rose-50 border-rose-200" data-testid="pda-stocktake-missing">
            <div className="px-3 py-2 border-b border-rose-200">
              <div className="flex items-center justify-between text-rose-800">
                <div className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Missing from {isHotel ? "this hotel" : "warehouse"}
                </div>
                <div className="font-display font-bold text-2xl">{diff.missingCount}</div>
              </div>
              <div className="text-[11px] text-rose-700 mt-1">
                Flagged "missing" on commit; auto-recovers when scanned anywhere later.
              </div>
            </div>
            {diff.missingRows.length > 0 && (
              <div className="divide-y divide-rose-200">
                {diff.missingRows.map((g) => (
                  <div key={g.product_id} className="px-3 py-1.5 flex items-center justify-between">
                    <span className="text-sm font-medium text-rose-900">{g.product_name}</span>
                    <span className="font-mono tabular-nums font-bold text-sm text-rose-800">{g.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PdaLayout>
  );
}

function groupByProduct(list) {
  if (!list) return [];
  const m = new Map();
  for (const it of list) {
    const key = it.product_id || "unknown";
    const cur = m.get(key) || { product_id: it.product_id, product_name: it.product_name || "Unknown", items: [] };
    cur.items.push(it);
    m.set(key, cur);
  }
  return Array.from(m.values()).sort((a, b) => b.items.length - a.items.length);
}

function summariseLocations(items) {
  const counts = new Map();
  for (const it of items) {
    const loc = it.status === "at_warehouse" ? "Warehouse" : (it.current_hotel_name || "Unknown hotel");
    counts.set(loc, (counts.get(loc) || 0) + 1);
  }
  return Array.from(counts.entries()).map(([loc, n]) => `${loc} × ${n}`).join(" · ");
}

function TopStat({ label, value, subtle, tone, testid }) {
  const cls = {
    neutral: "border-neutral-200 bg-white text-neutral-800",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    rose:    "border-rose-300 bg-rose-50 text-rose-800",
  }[tone] || "border-neutral-200 bg-white text-neutral-800";
  return (
    <div className={`rounded-md border px-2 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
      <div className="font-display font-bold text-3xl leading-none mt-0.5">{value}</div>
      {subtle ? <div className="text-[10px] opacity-70 mt-1 truncate">{subtle}</div> : null}
    </div>
  );
}

function UnknownCollapsible({ unknown, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border bg-amber-50 border-amber-200" data-testid={testid}>
      <button onClick={() => setOpen((v) => !v)} className="w-full px-3 py-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5" /> Unknown tags · {unknown.length}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-amber-900" /> : <ChevronDown className="h-3.5 w-3.5 text-amber-900" />}
      </button>
      {open && (
        <div className="max-h-40 overflow-y-auto divide-y divide-amber-200 border-t border-amber-200">
          {unknown.map((r) => (
            <div key={r} className="px-3 py-1.5 text-[11px] font-mono">{r}</div>
          ))}
        </div>
      )}
    </div>
  );
}
